import type { ComponentProps, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { useLocale } from '@/lib/i18n/locale'
import { formatCount } from '@hk/core'

/* FilterChip — the pill row above a board or list.

   The count is part of the control and MUST be computed from the same data the
   filter returns. A chip reading "Dirty 17" beside a filter that yields 18 rows
   is the fastest way to lose a user's trust in the whole screen, and it is the
   defect that took longest to spot during the screen build. Derive it; never
   type it. */

export interface FilterChipProps extends Omit<ComponentProps<'button'>, 'children'> {
  label: ReactNode
  count?: number
  active?: boolean
  swatch?: string
}

export function FilterChip({ label, count, active, swatch, className, ...props }: FilterChipProps) {
  const { locale } = useLocale()
  return (
    <button
      type="button"
      aria-pressed={active}
      className={cn(
        'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium cursor-pointer',
        active
          ? 'bg-primary border-primary text-primary-fg'
          : 'bg-card border-border text-muted-fg hover:border-primary hover:text-primary',
        className,
      )}
      {...props}
    >
      {swatch && (
        <span
          aria-hidden
          className="size-2.5 shrink-0 rounded-[3px] border-2"
          style={{ borderColor: swatch }}
        />
      )}
      {label}
      {count !== undefined && (
        <span className="tnum opacity-85">{formatCount(count, locale)}</span>
      )}
    </button>
  )
}
